import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Icon } from "@/components/ui/icon";
import { AuthLayout, GoogleButton, OrDivider } from "@/components/auth/AuthLayout";

const schema = z.object({
  email: z.string().trim().email("Enter a valid email"),
  password: z.string().min(8, "Password must be at least 8 characters"),
});

type FormData = z.infer<typeof schema>;

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);

  const oauthError = searchParams.get("error");
  const oauthErrorMessages: Record<string, string> = {
    oauth_denied: "Google sign-in was cancelled.",
    token_exchange_failed: "Failed to complete sign-in. Please try again.",
    email_not_verified: "Your Google email is not verified.",
    oauth_error: "Something went wrong with Google sign-in.",
    no_token: "Sign-in failed — no token received.",
  };

  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    const result = await login(data.email, data.password);
    setIsLoading(false);
    if (result.error) toast.error(result.error);
    else navigate("/dashboard");
  };

  return (
    <AuthLayout title="Welcome back" subtitle="Sign in to see what your competitors changed.">
      {oauthError && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 space-y-2">
          <p className="font-sans text-xs text-destructive">
            {oauthErrorMessages[oauthError] ?? "Sign-in failed. Please try again."}
          </p>
          {oauthError === "no_token" && (
            <p className="font-sans text-xs text-muted-foreground">
              Try clicking "Continue with Google" again — this sometimes happens on the first attempt.
            </p>
          )}
        </div>
      )}

      <GoogleButton />
      <OrDivider />

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="email" className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
            Email
          </Label>
          <Input id="email" type="email" autoComplete="email" placeholder="you@company.com" {...register("email")} />
          {errors.email && <p className="font-sans text-xs text-destructive">{errors.email.message}</p>}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="password" className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
            Password
          </Label>
          <Input id="password" type="password" autoComplete="current-password" placeholder="••••••••" {...register("password")} />
          {errors.password && <p className="font-sans text-xs text-destructive">{errors.password.message}</p>}
        </div>

        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading && <Icon name="refresh" size={12} className="mr-2 animate-spin" />}
          Sign in
        </Button>
      </form>

      <p className="font-sans text-xs text-muted-foreground">
        Don't have an account?{" "}
        <a href="https://peersheep.com/contact" className="text-foreground hover:underline font-medium">
          Talk to sales
        </a>
      </p>
    </AuthLayout>
  );
}
